import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import {
  ListSalesInvoicesFilters,
  MerchantService,
  PaymentStatusType,
  SalesInvoice,
  SalesInvoiceService,
  SalesInvoiceType,
} from '@galaxy/billing';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ConfirmationData, ConfirmationService } from '@vendasta/uikit';
import { FilterControl, FilterGroup } from '@vendasta/va-filter2';
import { BehaviorSubject, EMPTY, Observable, combineLatest as ObservableCombineLatest, Subscription, from } from 'rxjs';
import {
  catchError,
  map,
  filter as observableFilter,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { BillingErpUrlService } from '../billing-erp-url.service';
import { ChargeInvoiceDialogComponent } from '../charge-invoice-dialog/charge-invoice-dialog.component';
import { downloadFile, viewFile } from '../shared/file';
import {
  SalesInvoiceListDataSource,
  SalesInvoiceListDataSourceLoadingContext,
  SalesInvoiceListDatasourceFilters,
} from './sales-invoice-list-datasource';

@Component({
  selector: 'billing-ui-sales-invoice-list',
  templateUrl: './sales-invoice-list.component.html',
  styleUrls: ['./sales-invoice-list.component.scss', '../shared/shared-styles.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SalesInvoiceListDataSource],
  standalone: false,
})
export class SalesInvoiceListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('downloadAnchor', { static: true }) private downloadAnchor: ElementRef;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  // Filters for Looking up the Sales Invoices
  @Input() filters: Observable<ListSalesInvoicesFilters>;
  // Columns to display
  @Input() displayColumns: string[];
  @Input() displayForAdmin? = false;

  private readonly invoicePDFDownloadMap = new Map<string, BehaviorSubject<boolean>>();
  private readonly invoiceLoadingMap = new Map<string, BehaviorSubject<boolean>>();
  private readonly invoiceEmailSendingMap = new Map<string, BehaviorSubject<boolean>>();
  private readonly invoicePostingMap = new Map<string, BehaviorSubject<boolean>>();

  filterGroup: FilterGroup;

  _filters$: Observable<SalesInvoiceListDatasourceFilters>;

  paymentStatuses = [
    {
      display: 'Paid',
      value: PaymentStatusType.Paid,
    },
    {
      display: 'Part Paid',
      value: PaymentStatusType.Part_Paid,
    },
    {
      display: 'Unpaid',
      value: PaymentStatusType.Unpaid,
    },
  ];

  public length$: Observable<number>;

  viewPDFClicks = new Map<string, number>();

  allColumns = [
    'account',
    'invoiceNumber',
    'externalId',
    'period',
    'invoiceDate',
    'dueDate',
    'type',
    'paymentStatus',
    'outstanding',
    'total',
    'actions',
  ];
  allColumnsNames = new Map([
    ['account', 'Account'],
    ['invoiceNumber', 'Invoice #'],
    ['externalId', 'External ID'],
    ['period', 'Period'],
    ['invoiceDate', 'Invoice Date'],
    ['dueDate', 'Due Date'],
    ['type', 'Type'],
    ['paymentStatus', 'Payment Status'],
    ['outstanding', 'Outstanding'],
    ['total', 'Total'],
    ['actions', 'Actions'],
  ]);

  // DisplayColumnsNames are the mapped name values of the displayColumns
  displayColumnsNames: string[];

  subscriptions: Subscription[] = [];

  constructor(
    private salesInvoiceService: SalesInvoiceService,
    private alertService: SnackbarService,
    private merchantService: MerchantService,
    public dialog: MatDialog,
    private confirmation: ConfirmationService,
    public dataSource: SalesInvoiceListDataSource,
    public billingErpUrlService: BillingErpUrlService,
  ) {
    this.filterGroup = new FilterGroup('invoice-list', [
      new FilterControl('invoiceId', undefined, undefined, {
        appliedValueMapper: (name: string, value: string) => {
          return {
            name,
            label: `ID: ${value}`,
          };
        },
      }),
      new FilterControl('invoiceNumber'),
      new FilterControl('paymentStatus', undefined, undefined, {
        appliedValueMapper: (name: string, value: string) => {
          return {
            name,
            label: `Payment Status: ${this.paymentStatuses.find((s) => s.value === value).display}`,
          };
        },
      }),
    ]);
  }

  getLoadingContext$(): Observable<SalesInvoiceListDataSourceLoadingContext> {
    return this.dataSource.loadingContext$;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => {
      s.unsubscribe();
    });
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this._filters$.subscribe((filter) => {
        this.paginator.firstPage();
        this.dataSource.reload(filter, this.paginator.pageSize, this.paginator.pageIndex);
      }),
    );
  }

  ngOnInit(): void {
    const valueChanges$: Observable<SalesInvoiceListDatasourceFilters> = this.filterGroup.valueChanges.pipe(
      startWith({}),
      map((values) => ({
        merchantId: values.merchantId,
        paymentStatuses: values.paymentStatus ? [values.paymentStatus] : [],
        invoiceId: values.invoiceId ? [values.invoiceId] : [],
        searchTerm: values.invoiceNumber,
      })),
      shareReplay(1),
    );

    this._filters$ = ObservableCombineLatest([this.filters, valueChanges$]).pipe(
      map(([externalFilters, internalFilters]) => ({
        merchantId: externalFilters.merchantId || internalFilters.merchantId,
        statuses: externalFilters.statuses?.length ? externalFilters.statuses : internalFilters.statuses,
        paymentStatuses: externalFilters.paymentStatuses?.length
          ? externalFilters.paymentStatuses
          : internalFilters.paymentStatuses,
        invoiceId: externalFilters.invoiceId?.length ? externalFilters.invoiceId : internalFilters.invoiceId,
        searchTerm: internalFilters.searchTerm,
      })),
      shareReplay(1),
    );
    this.subscriptions.push(
      this.paginator.page.pipe(withLatestFrom(this._filters$)).subscribe(([, filter]) => {
        this.dataSource.updatePage(filter, this.paginator.pageSize, this.paginator.pageIndex);
      }),
    );

    this.length$ = this.dataSource.storedSalesInvoices$$.pipe(
      skip(1),
      tap((items) => {
        items?.forEach((item) => {
          if (!this.invoiceLoadingMap.has(item.invoiceId)) {
            this.invoiceLoadingMap.set(item.invoiceId, new BehaviorSubject<boolean>(false));
          }
          if (!this.invoicePDFDownloadMap.has(item.invoiceId)) {
            this.invoicePDFDownloadMap.set(item.invoiceId, new BehaviorSubject<boolean>(false));
          }
          if (!this.invoiceEmailSendingMap.has(item.invoiceId)) {
            this.invoiceEmailSendingMap.set(item.invoiceId, new BehaviorSubject<boolean>(false));
          }
          if (!this.invoicePostingMap.has(item.invoiceId)) {
            this.invoicePostingMap.set(item.invoiceId, new BehaviorSubject<boolean>(false));
          }
        });
      }),
      withLatestFrom(this.dataSource.hasMore$),
      map(([items, hasMore]) => (hasMore ? items.length + this.paginator.pageSize : items.length)),
    );

    this.displayColumns =
      this.displayColumns && this.displayColumns.length !== 0 ? this.displayColumns : this.allColumns;
    this.displayColumnsNames = this.displayColumns.map((column) => this.allColumnsNames.get(column));
  }
  formatPeriod(startDate: Date, endDate: Date): string {
    const monthDate = new Date(startDate);
    monthDate.setDate(monthDate.getDate() + 15);
    const month = monthDate.toLocaleString('en-US', { month: 'long' });
    const year = endDate.getFullYear();
    return `${month}, ${year}`;
  }

  isPDFLoading$(invoiceId: string): Observable<boolean> {
    return this.invoicePDFDownloadMap?.get(invoiceId).asObservable();
  }

  isEmailLoading$(invoiceId: string): Observable<boolean> {
    return this.invoiceLoadingMap?.get(invoiceId).asObservable();
  }

  isInvoicePosting$(invoiceId: string): Observable<boolean> {
    return this.invoicePostingMap?.get(invoiceId).asObservable();
  }

  isInvoiceEmailReceiptSending$(invoiceId: string): Observable<boolean> {
    return this.invoiceEmailSendingMap?.get(invoiceId).asObservable();
  }

  isRowWorking$(invoiceId: string): Observable<boolean> {
    return ObservableCombineLatest([
      this.isEmailLoading$(invoiceId),
      this.isPDFLoading$(invoiceId),
      this.isInvoicePosting$(invoiceId),
      this.isInvoiceEmailReceiptSending$(invoiceId),
    ]).pipe(
      map(
        ([isEmailLoading, isPDFLoading, isInvoicePosting, isInvoiceEmailReceiptSending]) =>
          isEmailLoading || isPDFLoading || isInvoicePosting || isInvoiceEmailReceiptSending,
      ),
    );
  }

  formatType(type: SalesInvoiceType): string {
    switch (type) {
      case SalesInvoiceType.Instant:
        return 'Instant';
      case SalesInvoiceType.Invoiced:
        return 'Invoiced';
      default:
        return '';
    }
  }

  download(salesInvoice: SalesInvoice): void {
    this.invoicePDFDownloadMap.get(salesInvoice.invoiceId)?.next(true);
    this.salesInvoiceService
      .downloadPdf(salesInvoice.invoiceId, salesInvoice.merchantId)
      .pipe(
        tap((file) => downloadFile(this.downloadAnchor, file, salesInvoice.invoiceNumber, 'application/pdf')),
        catchError((err) => {
          this.alertService.openErrorSnack('Failed to download invoice PDF');
          this.invoicePDFDownloadMap.get(salesInvoice.invoiceId)?.next(false);
          throw err;
        }),
        take(1),
      )
      .subscribe(() => this.invoicePDFDownloadMap.get(salesInvoice.invoiceId)?.next(false));
  }

  view(salesInvoice: SalesInvoice): void {
    this.invoicePDFDownloadMap.get(salesInvoice.invoiceId)?.next(true);
    const currClicks = this.viewPDFClicks.get(salesInvoice.invoiceId);
    this.viewPDFClicks.set(salesInvoice.invoiceId, currClicks ? currClicks + 1 : 1);
    this.salesInvoiceService
      .downloadPdf(salesInvoice.invoiceId, salesInvoice.merchantId)
      .pipe(
        tap((file) => viewFile(file, 'application/pdf')),
        catchError((err) => {
          this.alertService.openErrorSnack('Failed to get invoice PDF');
          this.invoicePDFDownloadMap.get(salesInvoice.invoiceId)?.next(false);
          throw err;
        }),
        take(1),
      )
      .subscribe(() => this.invoicePDFDownloadMap.get(salesInvoice.invoiceId)?.next(false));
  }

  sendSalesInvoiceEmail(invoice: SalesInvoice): void {
    const options: ConfirmationData = {
      title: 'Are you sure you want to send this email?',
      text: 'This will send a copy of the invoice to all the email addresses associated with the billing contact.',
      confirmText: 'Yes',
      closeText: 'Cancel',
    };
    this.sendSalesInvoiceEmailConfirmed(invoice, options);
  }

  sendSalesInvoiceEmailConfirmed(invoice: SalesInvoice, options: ConfirmationData): void {
    this.invoiceLoadingMap.get(invoice.invoiceId)?.next(true);
    from(this.confirmation.confirm(options))
      .pipe(
        observableFilter((confirmationClicked) => confirmationClicked),
        switchMap(() => this.merchantService.sendSalesInvoiceEmail(invoice.merchantId, invoice.invoiceId)),
        shareReplay(1),
        catchError((err) => {
          this.alertService.openErrorSnack(`Failed to send invoice email`);
          this.invoiceLoadingMap.get(invoice.invoiceId)?.next(false);
          throw err;
        }),
        take(1),
      )
      .subscribe(() => {
        this.invoiceLoadingMap.get(invoice.invoiceId)?.next(false);
        this.alertService.openSuccessSnack(`Successfully sent invoice email`);
      });
  }

  public chargeSalesInvoice(invoice: SalesInvoice): void {
    const chargeDialogConfig = {
      width: '450px',
      data: {
        merchantId: invoice.merchantId,
        invoiceId: invoice.invoiceId,
        outstandingBalance: invoice.outstanding || 0,
        invoiceNumber: invoice.invoiceNumber,
        totalAmount: invoice.total,
        invoiceCurrency: invoice.currency,
      },
    } as MatDialogConfig;

    if (invoice.type === SalesInvoiceType.Instant) {
      const options: ConfirmationData = {
        title: 'Are you sure you want to charge this invoice?',
        text:
          'This is an instant purchase invoice. Any payments made to this invoice will not be reflected on the purchase record.' +
          'Overcharges are possible if the purchase is retried. Are you sure you want to continue?',
        confirmText: 'Continue',
        closeText: 'Cancel',
      };

      from(this.confirmation.confirm(options))
        .pipe(
          switchMap((confirmationClicked) =>
            confirmationClicked
              ? this.dialog.open(ChargeInvoiceDialogComponent, chargeDialogConfig).afterClosed()
              : EMPTY,
          ),
        )
        .subscribe();
    } else {
      this.dialog.open(ChargeInvoiceDialogComponent, chargeDialogConfig).afterClosed().subscribe();
    }
  }

  openSendSalesInvoiceReceiptEmailDialog(invoice: SalesInvoice): void {
    const options: ConfirmationData = {
      title: 'Are you sure you want to send this email?',
      text:
        'This will send a copy of the invoice receipt for the most recent payment to' +
        'all the email addresses associated with the billing contact.',
      confirmText: 'Yes',
      closeText: 'Cancel',
    };
    this.sendSalesInvoiceReceiptEmail(invoice, options);
  }

  public getSalesInvoiceDetails(invoiceId: string): string {
    return this.billingErpUrlService.getSalesInvoiceDetails(invoiceId);
  }

  public sendSalesInvoiceReceiptEmail(invoice: SalesInvoice, options: ConfirmationData): void {
    this.invoiceEmailSendingMap.get(invoice.invoiceId).next(true);
    from(this.confirmation.confirm(options))
      .pipe(
        observableFilter((confirmationClicked) => confirmationClicked),
        switchMap(() => this.merchantService.sendSalesInvoiceReceiptEmail(invoice.merchantId, invoice.invoiceId)),
        catchError((err) => {
          const errorMessage = 'Failed to send receipt email with error ' + (err?.error?.message ?? '');
          this.alertService.openErrorSnack(errorMessage);
          this.invoiceEmailSendingMap.get(invoice.invoiceId).next(false);
          throw err;
        }),
        take(1),
      )
      .subscribe(() => {
        this.alertService.openSuccessSnack(`Successfully sent receipt email`);
        this.invoiceEmailSendingMap.get(invoice.invoiceId).next(false);
      });
  }
}
